import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Currency } from '@/api/types/generated';
import { LogoMark } from '@/components/Ui/Logo';
import { NavCount } from '@/components/Ui/NavCount';
import { useAccountIdentity } from '@/hooks/useAccountIdentity';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { formatCredit } from '@/lib/money';
import { planLabel } from '@/pages/Account/Billing/billing-labels';
import { useRustyState } from '../../state';
import classes from './AccountNavigation.module.css';

interface NavItem {
  path: string;
  label: string;
  /** Also active on sub-paths. */
  deep?: boolean;
  /** Extra paths that count as this item, e.g. the SSO link landing under Sign-in & security. */
  also?: string[];
  count?: ReactNode;
}

export interface AccountNavigationProps {
  /** Called after any choice, so the mobile drawer can close. */
  onNavigate?: () => void;
}

export function AccountNavigation({ onNavigate }: AccountNavigationProps) {
  const { t, i18n } = useTranslation();
  const { createLanguageURL, isActive } = useLanguageAwareRouting();
  const unreadCount = useRustyState((s) => s.unreadCount);
  const problemsCount = useRustyState((s) => s.problemsCount);
  const subscription = useRustyState((s) => s.currentSubscription);
  const balance = useRustyState((s) => s.accountBalance);
  const identity = useAccountIdentity().label;

  const euro = balance?.find(([currency]) => currency === Currency.EUR);
  const credit = euro ? formatCredit(euro[1], i18n.language) : null;
  // Same bare name as Home and Billing ("Free", not "Free plan").
  const plan = subscription?.plan_name ? planLabel(subscription.plan_name) : undefined;
  const planLine =
    plan && credit
      ? t('nav.planAndCredit', { plan, credit })
      : plan
        ? t('nav.planOnly', { plan })
        : credit
          ? t('nav.creditOnly', { credit })
          : null;

  const workspace: NavItem[] = [
    { path: '/account', label: t('nav.home') },
    { path: '/account/forms', label: t('nav.forms'), deep: true },
    {
      path: '/account/messages',
      label: t('nav.messages'),
      deep: true,
      count: unreadCount ? <NavCount>{t('nav.unread', { count: unreadCount })}</NavCount> : null,
    },
    {
      path: '/account/problems',
      label: t('nav.problems'),
      deep: true,
      count: problemsCount ? (
        <NavCount variant="risk" label={t('nav.problemsCount', { count: problemsCount })}>
          {problemsCount}
        </NavCount>
      ) : null,
    },
    { path: '/account/integrations', label: t('nav.integrations'), deep: true },
  ];

  const account: NavItem[] = [
    { path: '/account/billing', label: t('nav.billing'), deep: true },
    { path: '/account/addresses', label: t('nav.addresses'), deep: true },
    { path: '/account/security', label: t('nav.security'), deep: true, also: ['/account/linked'] },
    { path: '/account/developers', label: t('nav.developers'), deep: true },
  ];

  const help: NavItem[] = [
    { path: '/account/docs', label: t('nav.docs'), deep: true },
    { path: '/account/support', label: t('nav.support'), deep: true },
  ];

  const signOut = async () => {
    onNavigate?.();
    if (!(await useRustyState.getState().logout())) {
      window.location.replace(createLanguageURL('/'));
    }
  };

  const renderItem = (item: NavItem) => {
    const current =
      isActive(item.path, item.deep) || (item.also ?? []).some((p) => isActive(p, true));
    return (
      <Link
        key={item.path}
        to={createLanguageURL(item.path)}
        className={classes.link}
        aria-current={current ? 'page' : undefined}
        onClick={onNavigate}
      >
        <span>{item.label}</span>
        {item.count}
      </Link>
    );
  };

  return (
    <nav aria-label={t('nav.main')} className={classes.side}>
      <Link to={createLanguageURL('/account')} className={classes.brand} onClick={onNavigate}>
        <LogoMark />
        <span className={classes.brandName}>{t('nav.brand')}</span>
      </Link>

      <div className={classes.group}>{workspace.map(renderItem)}</div>

      <div className={classes.group}>
        <div className={classes.heading}>{t('nav.account')}</div>
        {account.map(renderItem)}
      </div>

      <div className={classes.group}>
        <div className={classes.heading}>{t('nav.help')}</div>
        {help.map(renderItem)}
      </div>

      <div className={classes.footer}>
        {identity && <span className={classes.identity}>{identity}</span>}
        {planLine && <span className={classes.plan}>{planLine}</span>}
        <button type="button" className={classes.signOut} onClick={() => void signOut()}>
          {t('nav.signOut')}
        </button>
      </div>
    </nav>
  );
}
